import React, { useEffect, useMemo, useState } from "react";
import {
  getDebugEntries,
  isDebugEnabled,
  subscribeToDebug,
  disableDebug,
  clearDebug,
  dumpDebugText,
} from "../lib/debugMode";

/**
 * Floating debug console overlay — only renders when debug mode is on
 * (toggled by entering the magic activation key "logtest").
 *
 * v1.3.18: filter box, copy-all, clear, level toggles, wider viewport.
 */
const LEVELS = ["log", "info", "warn", "error"];
const LEVEL_COLOR = {
  log: "#cbd5e1",
  info: "#93c5fd",
  warn: "#fcd34d",
  error: "#fca5a5",
};

export default function DebugConsole() {
  const [enabled, setEnabled] = useState(() => isDebugEnabled());
  const [entries, setEntries] = useState(() => getDebugEntries());
  const [collapsed, setCollapsed] = useState(false);
  const [filter, setFilter] = useState("");
  const [enabledLevels, setEnabledLevels] = useState(() => new Set(LEVELS));
  const [paused, setPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);

  useEffect(() => {
    return subscribeToDebug((snap, on) => {
      if (!paused) setEntries(snap);
      setEnabled(on);
    });
  }, [paused]);

  // Auto-scroll to bottom on new entries when enabled.
  useEffect(() => {
    if (!autoScroll || collapsed) return;
    const el = document.getElementById("zapp-debug-list");
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries, autoScroll, collapsed]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return entries.filter((e) => {
      if (!enabledLevels.has(e.level)) return false;
      if (!f) return true;
      return (
        (e.msg || "").toLowerCase().includes(f) ||
        (e.tag || "").toLowerCase().includes(f)
      );
    });
  }, [entries, filter, enabledLevels]);

  if (!enabled) return null;

  function toggleLevel(l) {
    setEnabledLevels((prev) => {
      const next = new Set(prev);
      if (next.has(l)) next.delete(l); else next.add(l);
      return next;
    });
  }

  async function copyAll() {
    const text = dumpDebugText();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Capacitor / older WebViews — fall back to a textarea trick.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
  }

  return (
    <div
      data-testid="debug-console"
      style={{
        position: "fixed",
        bottom: 8,
        right: 8,
        zIndex: 9999,
        width: collapsed ? 220 : Math.min(640, window.innerWidth - 16),
        maxWidth: "calc(100vw - 16px)",
        height: collapsed ? 32 : Math.min(560, window.innerHeight - 24),
        maxHeight: "82vh",
        background: "rgba(8, 10, 14, 0.96)",
        border: "1px solid rgba(255,200,50,0.5)",
        borderRadius: 6,
        color: "#e5e7eb",
        fontFamily: "monospace",
        fontSize: 11,
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 4px 24px rgba(0,0,0,0.6)",
        pointerEvents: "auto",
      }}
    >
      {/* Title bar */}
      <div
        onClick={() => setCollapsed((c) => !c)}
        style={{
          padding: "4px 8px",
          background: "rgba(255,200,50,0.15)",
          color: "#fde047",
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          userSelect: "none",
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
        }}
      >
        <span>DEBUG · {filtered.length}/{entries.length}</span>
        <div style={{ display: "flex", gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <ToolBtn label={collapsed ? "▢" : "▭"} onClick={() => setCollapsed((c) => !c)} title="Collapse" />
          <ToolBtn
            label="×"
            onClick={() => {
              if (window.confirm("Turn debug mode off?")) disableDebug();
            }}
            title="Disable debug"
          />
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Filter + actions */}
          <div
            style={{
              padding: "4px 6px",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              display: "flex",
              gap: 4,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="filter… (e.g. cover, fetch, xtream)"
              style={{
                flex: 1,
                minWidth: 80,
                background: "rgba(255,255,255,0.05)",
                color: "#e5e7eb",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 3,
                padding: "2px 6px",
                fontSize: 11,
                fontFamily: "monospace",
              }}
            />
            {LEVELS.map((l) => (
              <button
                key={l}
                onClick={() => toggleLevel(l)}
                title={`Toggle ${l}`}
                style={{
                  background: enabledLevels.has(l)
                    ? `${LEVEL_COLOR[l]}33`
                    : "transparent",
                  color: enabledLevels.has(l) ? LEVEL_COLOR[l] : "#475569",
                  border: `1px solid ${enabledLevels.has(l) ? LEVEL_COLOR[l] + "55" : "rgba(255,255,255,0.1)"}`,
                  borderRadius: 3,
                  padding: "1px 6px",
                  fontSize: 10,
                  cursor: "pointer",
                  fontFamily: "monospace",
                }}
              >
                {l}
              </button>
            ))}
            <ToolBtn label={paused ? "▶" : "‖"} onClick={() => setPaused(!paused)} title="Pause/resume" />
            <ToolBtn label="↧" onClick={() => setAutoScroll(!autoScroll)} title={autoScroll ? "Auto-scroll ON" : "Auto-scroll OFF"} active={autoScroll} />
            <ToolBtn label="Copy" onClick={copyAll} title="Copy all to clipboard" />
            <ToolBtn label="Clear" onClick={() => { clearDebug(); setEntries([]); }} title="Clear buffer" />
          </div>

          {/* Entries */}
          <div
            id="zapp-debug-list"
            style={{
              overflowY: "auto",
              padding: "4px 6px",
              flex: 1,
              minHeight: 0,
            }}
          >
            {filtered.length === 0 ? (
              <div style={{ color: "#64748b", padding: 4 }}>
                {entries.length === 0 ? "(no log entries yet)" : "(nothing matches filter)"}
              </div>
            ) : (
              filtered.map((e, i) => (
                <div
                  key={i}
                  style={{
                    color: LEVEL_COLOR[e.level] || "#cbd5e1",
                    borderBottom: "1px solid rgba(255,255,255,0.04)",
                    padding: "2px 0",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "monospace",
                  }}
                >
                  <span style={{ color: "#64748b", marginRight: 4 }}>
                    {new Date(e.ts).toISOString().slice(11, 23)}
                  </span>
                  {e.msg}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ToolBtn({ label, onClick, title, active }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: active ? "rgba(34, 211, 238, 0.15)" : "transparent",
        border: "1px solid rgba(255,255,255,0.1)",
        color: active ? "#22d3ee" : "#fde047",
        cursor: "pointer",
        fontSize: 11,
        lineHeight: 1,
        padding: "2px 6px",
        borderRadius: 3,
        fontFamily: "monospace",
      }}
    >
      {label}
    </button>
  );
}
